/**
 * PredefinedValueEditor Component
 *
 * A reusable editor component for managing predefined values with optional color categorization.
 * This component is designed to handle various data types that have a finite set of predefined values.
 *
 * Use cases:
 * - Enums: Fixed set of enumerated values (e.g., Priority levels: High, Medium, Low)
 * - Tags: Categorization labels with visual color coding
 * - Statuses: Workflow states with color-coded visual indicators (e.g., Active, Inactive, Pending)
 */
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";

import { Input, InputIconProps } from "src/base-components/Input";
import { ColorSelector } from "src/base-components/schemaKit/ColorSelector";
import { castColor } from "src/base-components/schemaKit/schemaColors";
import { ErrorHint } from "src/design-system/ErrorHint";

type InputName = "value" | "color";

type FormFieldParams = {
  name: string;
  rules?: RegisterOptions;
};

type PredefinedValueEditorProps = {
  disabled?: boolean | InputName[];
  error?: string | false;
  dataLoc?: string;
  suffixIcon?: InputIconProps;
  valueField: FormFieldParams;
} & (
  | {
      hideColorSelector: true;
      colorField?: FormFieldParams;
    }
  | {
      hideColorSelector?: false;
      colorField: FormFieldParams;
    }
);

export const PredefinedValueEditor = ({
  hideColorSelector,
  disabled,
  error,
  dataLoc,
  suffixIcon,
  valueField: valueForm,
  colorField: colorForm,
}: PredefinedValueEditorProps) => {
  const form = useFormContext();
  const disabledFields = getDisabledFields(disabled);
  return (
    <div className="flex items-start gap-x-2">
      {!hideColorSelector && (
        <div>
          <Controller
            defaultValue="gray"
            name={colorForm.name}
            render={({ field }) => (
              <ColorSelector
                disabled={disabledFields.color}
                value={castColor(field.value)}
                onChange={field.onChange}
              />
            )}
            rules={colorForm.rules}
          />
        </div>
      )}
      <div className="flex-1">
        <Input
          data-loc={dataLoc ? `${dataLoc}-value` : undefined}
          disabled={disabledFields.value}
          errored={!!error}
          suffixIcon={suffixIcon}
          fullWidth
          monospaced
          {...form.register(valueForm.name, valueForm.rules)}
        />
        {error && <ErrorHint>{error}</ErrorHint>}
      </div>
    </div>
  );
};

const getDisabledFields = (disabled?: boolean | InputName[]) => {
  return typeof disabled === "boolean"
    ? {
        value: disabled,
        color: disabled,
      }
    : {
        value: disabled?.includes("value"),
        color: disabled?.includes("color"),
      };
};
